import {type FormEvent, useState} from "react";
import {BaseAlert, BaseBreadcrumb, BaseButton, BaseCard, BaseInput, BaseSelect} from "@/components";
import {BANK_OPTIONS} from "@/pages/private/DepositPagoMovil/banks";
import {type PagoMovilAccountFormInput, pagoMovilAccountSchema} from "./pagoMovilAccount.schema";
import type {PagoMovilAccount} from "./pagoMovilAccounts.types";
import {
  useCreatePagoMovilAccount,
  useDeletePagoMovilAccount,
  usePagoMovilAccounts,
  useUpdatePagoMovilAccount,
} from "./usePagoMovilAccounts";

type FieldErrors = Partial<Record<keyof PagoMovilAccountFormInput, string>>;

const EMPTY_FORM: PagoMovilAccountFormInput = {bankCode: "", phoneNumber: ""};

const PagoMovilAccountsPage = () => {
  const {accounts, isLoading, isError, error, refetch} = usePagoMovilAccounts();
  const createMutation = useCreatePagoMovilAccount();
  const updateMutation = useUpdatePagoMovilAccount();
  const deleteMutation = useDeletePagoMovilAccount();

  const [form, setForm] = useState<PagoMovilAccountFormInput>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [editingId, setEditingId] = useState<string | null>(null);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const serverError = createMutation.error ?? updateMutation.error ?? deleteMutation.error;

  const handleEdit = (account: PagoMovilAccount) => {
    setEditingId(account.id);
    setForm({bankCode: String(Number(account.bankCode)), phoneNumber: account.phoneNumber});
    setErrors({});
    createMutation.reset();
    updateMutation.reset();
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErrors({});
  };

  const updateField = <K extends keyof PagoMovilAccountFormInput>(key: K, value: PagoMovilAccountFormInput[K]) => {
    setForm(prev => ({...prev, [key]: value}));
    setErrors(prev => ({...prev, [key]: undefined}));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = pagoMovilAccountSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof PagoMovilAccountFormInput | undefined;
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    const payload = {
      bank_code: Number(parsed.data.bankCode),
      phone_number: Number(parsed.data.phoneNumber),
    };

    if (editingId) {
      updateMutation.mutate({ID: editingId, ...payload}, {onSuccess: handleCancel});
    } else {
      createMutation.mutate(payload, {onSuccess: handleCancel});
    }
  };

  const handleDelete = (account: PagoMovilAccount) => {
    if (!window.confirm(`¿Eliminar la cuenta ${account.bankName} · ${account.phoneNumber}?`)) {
      return;
    }
    deleteMutation.mutate(account.id);
  };

  return (
    <section className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 py-8">
      <BaseBreadcrumb items={[{label: "Cuentas Pago Móvil"}]} />
      <header className="flex flex-col gap-2">
        <h1 className="text-[var(--font-h1)] font-bold text-[var(--color-text)]">Cuentas Pago Móvil</h1>
        <p className="text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
          Administra los bancos y teléfonos que usas para depositar y retirar.
        </p>
      </header>

      {serverError ? (
        <BaseAlert
          variant="danger"
          title="Ocurrió un error"
        >
          <p>{serverError.message}</p>
        </BaseAlert>
      ) : null}

      <BaseCard
        variant="surface"
        padding="lg"
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit}
          noValidate
        >
          <h2 className="text-[var(--font-h3)] font-semibold text-[var(--color-text)]">
            {editingId ? "Editar cuenta" : "Agregar nueva cuenta"}
          </h2>
          <BaseSelect
            label="Banco"
            placeholder="Selecciona un banco"
            options={BANK_OPTIONS}
            value={form.bankCode}
            onChange={e => updateField("bankCode", e.target.value)}
            error={errors.bankCode}
            disabled={isSubmitting}
          />
          <BaseInput
            label="Teléfono"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="04121234567"
            value={form.phoneNumber}
            onChange={e => updateField("phoneNumber", e.target.value)}
            error={errors.phoneNumber}
            disabled={isSubmitting}
          />
          <div className="flex flex-col gap-2 sm:flex-row">
            <BaseButton
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              fullWidth
            >
              {isSubmitting ? "Guardando..." : editingId ? "Actualizar" : "Agregar cuenta"}
            </BaseButton>
            {editingId ? (
              <BaseButton
                type="button"
                variant="ghost"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancelar
              </BaseButton>
            ) : null}
          </div>
        </form>
      </BaseCard>

      <div className="flex flex-col gap-3">
        <h2 className="text-[var(--font-h2)] font-semibold text-[var(--color-text)]">Mis cuentas</h2>
        {isLoading ? (
          <BaseCard
            variant="surface"
            padding="lg"
          >
            <p className="text-center text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">Cargando...</p>
          </BaseCard>
        ) : isError ? (
          <BaseAlert
            variant="danger"
            title="No pudimos cargar tus cuentas"
          >
            <p>{error?.message ?? "Inténtalo nuevamente."}</p>
            <BaseButton
              variant="secondary"
              size="sm"
              onClick={() => refetch()}
            >
              Reintentar
            </BaseButton>
          </BaseAlert>
        ) : accounts.length === 0 ? (
          <BaseCard
            variant="surface"
            padding="lg"
          >
            <p className="text-center text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
              Aún no tienes cuentas registradas.
            </p>
          </BaseCard>
        ) : (
          <ul className="flex flex-col gap-3">
            {accounts.map(account => (
              <li key={account.id}>
                <BaseCard
                  variant="surface"
                  padding="md"
                  className="flex items-center justify-between gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[var(--font-body-lg)] font-semibold text-[var(--color-text)]">
                      {account.bankName}
                    </p>
                    <p className="text-[var(--font-body)] text-[var(--color-text-tertiary)]">{account.phoneNumber}</p>
                  </div>
                  <div className="flex gap-2">
                    <BaseButton
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(account)}
                      disabled={isSubmitting || deleteMutation.isPending}
                    >
                      Editar
                    </BaseButton>
                    <BaseButton
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(account)}
                      disabled={deleteMutation.isPending}
                    >
                      Eliminar
                    </BaseButton>
                  </div>
                </BaseCard>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default PagoMovilAccountsPage;
